import React, { useCallback, useMemo, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import Animated from 'react-native-reanimated';
import { AppView, Button, ButtonGroup, CardView, rem, SCROLL_INDICATOR_INSETS, withAnimatedNavigationHeader, } from 'design-system';
import { HEIGHT } from 'design-system/Button';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import SectionHeader from 'design-system/SectionHeader';
import Spacer from 'design-system/Spacer';
import StatusBar from 'design-system/StatusBar';
import Text from 'design-system/Text';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import Modal from 'utils/packages/Modal';
import RowSubscription from '../components/row/RowSubscription';
import useDismissDuplicateGroup from '../hooks/useDismissDuplicateGroup';
import useDuplicateReviewStep from '../hooks/useDuplicateReviewStep';
import { isWeb } from '../../../constants';
const strings = {
    pageTitle: 'Already cancelled',
    title: (count) => count > 2 ? 'Which ones did you cancel?' : 'Which one did you cancel?',
    subTitle: (count) => count > 2
        ? 'We’ll stop tracking the ones you cancelled and keep monitoring the rest'
        : 'We’ll stop tracking the cancelled subscription and only monitor the active one',
    lastPayment: 'Last payment',
    continue: 'Continue',
    modalTitle: (count) => count > 1
        ? 'Deactivate these recurring payments?'
        : 'Deactivate this recurring payment?',
    modalCopy: 'You can still view your payment history anytime under inactive payments',
    goBack: 'Go back',
    successTitle: 'All sorted!',
    successSubTitle: (stopped, active) => `We’ve stopped tracking ${stopped > 1 ? 'those subscriptions' : 'that subscription'} and will keep monitoring your active ${active > 1 ? 'ones' : 'one'}`,
    done: 'Done',
};
/**
 * "I've already cancelled one": the user picks the group's subscriptions they
 * cancelled, at least one and never all of them, and confirms. Those are
 * deactivated and recorded as `already_cancelled`; the rest stay tracked. The
 * backend stops flagging a company once any of its subscriptions has a
 * dismissal reason, so the rest of the group isn't flagged again either.
 */
const DuplicateCancelledScreen = withAnimatedNavigationHeader(({ navigation, route, scrollHandler }) => {
    const { group, hasNextStep, goToNextStep } = useDuplicateReviewStep(navigation, route.params);
    const styles = useStyles(styleSet);
    const dismissGroup = useDismissDuplicateGroup();
    const [selectedIds, setSelectedIds] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    const selected = useMemo(() => group?.filter(({ id }) => selectedIds.includes(id)) ?? [], [group, selectedIds]);
    const canContinue = !!group && selected.length > 0 && selected.length < group.length;
    // Of a pair only one can have been cancelled, so it's a single choice
    // that moves with each tap; larger groups take several.
    const isPair = group?.length === 2;
    const toggle = useCallback((subscription) => {
        setSelectedIds((ids) => {
            if (isPair)
                return [subscription.id];
            return ids.includes(subscription.id)
                ? ids.filter((id) => id !== subscription.id)
                : [...ids, subscription.id];
        });
    }, [isPair]);
    const deactivate = useCallback(async () => {
        if (!group || isSaving)
            return;
        setIsSaving(true);
        const saved = await dismissGroup(selected, 'already_cancelled', {
            deactivate: true,
        });
        setIsSaving(false);
        if (!saved)
            return;
        navigation.navigateDeprecated('Success', {
            title: strings.successTitle,
            subTitle: strings.successSubTitle(selected.length, group.length - selected.length),
            buttonText: hasNextStep ? strings.continue : strings.done,
            onContinue: goToNextStep,
        });
    }, [
        group,
        isSaving,
        dismissGroup,
        selected,
        navigation,
        hasNextStep,
        goToNextStep,
    ]);
    const onContinue = useCallback(() => {
        if (!canContinue)
            return;
        Modal.showConfirmation({
            title: strings.modalTitle(selected.length),
            subtitle: (<View style={staticStyles.modalContent}>
              <Text TextThin-14 Secondary centered>
                {strings.modalCopy}
              </Text>
              <Spacer h={16}/>
              <View style={styles.modalRows}>
                {selected.map((subscription) => (<RowSubscription key={subscription.id} data={subscription} hideDuplicateLabel showLastPayment backgroundLight/>))}
              </View>
            </View>),
            confirmButtonTitle: strings.goBack,
            cancelButtonTitle: strings.continue,
            onConfirm: () => Modal.hide(),
            onPressCancel: () => {
                Modal.hide();
                deactivate();
            },
        });
    }, [canContinue, selected, styles.modalRows, deactivate]);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const paddingBottom = useMarginBottom('paddingBottom', HEIGHT + rem(16), paddingHorizontalStyle);
    if (!group)
        return null;
    return (<AppView withFrame={false}>
          <Animated.ScrollView scrollEventThrottle={16} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} onScroll={scrollHandler} contentContainerStyle={paddingBottom}>
            <StatusBar barStyle="dark-content" animated/>
            <ScreenInfoHeader title={strings.title(group.length)} subTitle={strings.subTitle(group.length)}/>
            <SectionHeader horizontalMargins>
              {strings.lastPayment}
            </SectionHeader>
            <CardView webPadding>
              {group.map((subscription) => (<RowSubscription key={subscription.id} data={subscription} hideDuplicateLabel showLastPayment selectable multiSelect={!isPair} selected={selectedIds.includes(subscription.id)} onPress={toggle}/>))}
            </CardView>
          </Animated.ScrollView>
          <ButtonGroup type="floating">
            <Button title={strings.continue} brand disabled={!canContinue} isFetching={isSaving} onPress={onContinue} testID="duplicate-cancelled-continue"/>
          </ButtonGroup>
        </AppView>);
}, ({ colors }) => ({
    title: '',
    pageTitle: strings.pageTitle,
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
const staticStyles = StyleSheet.create({
    // The sheet centres its content, so the rows would otherwise shrink to the
    // copy's width instead of lining up with the buttons.
    modalContent: {
        alignSelf: 'stretch',
    },
});
const styleSet = createStyleSheets((colors) => ({
    modalRows: {
        borderRadius: rem(16),
        overflow: 'hidden',
        backgroundColor: colors.background.dark,
        ...(isWeb && {
            padding: rem(8),
        }),
    },
}));
export default DuplicateCancelledScreen;
